import styles from "@/styles/onboarding.module.scss";
import { useEffect, useState } from "react";
import base64url from "base64url";
import { useRouter } from "next/router";
import {
  ADDRESS_DATA_FIELDS,
  ADDRESS_DATA_FIELDS_EMPTY,
} from "pages/credit-card/apply";
import NumberFormat from "react-number-format";
import {
  Container,
  Box,
  Button,
} from "@/components/onboardingSections/container";
import {
  BasicInput,
  CustomSelect,
  PostcodeSearch,
  StyledOption,
} from "@/components/onboardingSections/inputs";
import TagManager from "react-gtm-module";
import { generateKeyPair } from "@/components/auth/generateKeyPair";
import { CustomWarning } from "../elements/custom-warning";
import renderValue from "../elements/dropdown-placeholder";
import { checkValidDate, checkValidOldDate } from "utils/checkValidDate";
import { datadogRum } from "@datadog/browser-rum";
import { setDataInSessionStorage } from "utils/helper";
import { isValidEmail } from "utils/validators";
const crypto = require("crypto");

const calcAge = dateString => {
  var birthday = +new Date(dateString);
  return ~~((Date.now() - birthday) / 31557600000);
};

export default function Personal({
  personalData,
  addressData,
  completePersonal,
  verifyComplete,
}) {
  const [disabled, setDisabled] = useState(true);
  const [ageError, setAgeError] = useState(false);
  const [dobError, setDobError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [altAge, setAltAge] = useState(false);
  const [mobileError, setMobileError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [moveError, setMoveError] = useState(false);
  const [emptyError, setEmptyError] = useState(false);
  const [oldDateError, setOldDateError] = useState(false);

  const [addressArray, setAddressArray] = useState(addressData);
  const [returnedAddress, setReturnedAddress] = useState(null);
  const [personalArray, setPersonalArray] = useState(personalData);
  const [moveInDateDisplay, setMoveDateDisplay] = useState(null);
  const [dobDisplay, setDOBdisplay] = useState("");

  const router = useRouter();

  function parentArray(evt, arrayKey, type) {
    if (!evt?.target) return;
    const { value } = evt.target;
    if (type === "personal") {
      setPersonalArray(old => ({
        ...old,
        [arrayKey]: value,
      }));
    } else {
      if (arrayKey === "sub_building_number") {
        // need to update sub_building_name to reflect the building_number
        // i.e. if sub_building_number is 44, and sub_building_name is "Apartment 43",
        // then sub_building_name should be updated to "Apartment 44"
        const new_sub_building_name = addressArray?.sub_building_name?.replace(
          addressArray?.sub_building_number,
          value,
        );

        setAddressArray(old => ({
          ...old,
          sub_building_number: value,
          sub_building_name: new_sub_building_name,
        }));
      } else {
        setAddressArray(old => ({
          ...old,
          [arrayKey]: value,
        }));
      }
    }
  }

  useEffect(() => {
    if (!moveInDateDisplay) return;
    const dateEntered = moveInDateDisplay.split("/").map(e => e.trim());
    const moveInDate = dateEntered.reverse().join("-");

    setAddressArray(previous => ({
      ...previous,
      move_in_date: moveInDate,
    }));

    if (dateEntered.join("").length === 8) {
      const moveDate = new Date(moveInDate);
      const today = new Date();
      const isValidDate = checkValidDate(moveInDateDisplay);
      const isVeryOldDate = checkValidOldDate(moveInDateDisplay);
      if (moveDate > today || !isValidDate) {
        setMoveError(true);
      } else if (isVeryOldDate) {
        setOldDateError(true);
      } else {
        setMoveError(false);
        setOldDateError(false);
      }
    } else {
      setMoveError(false);
      setOldDateError(false);
    }
  }, [moveInDateDisplay]);

  useEffect(() => {
    const buildingName_And_buildingNumber_NotDefined =
      !addressArray?.building_name && !addressArray?.building_number;

    const subBuildingNumber_NotDefined_When_subBuildingName_Defined =
      !addressArray?.sub_building_number && addressArray?.sub_building_number;

    if (
      buildingName_And_buildingNumber_NotDefined ||
      subBuildingNumber_NotDefined_When_subBuildingName_Defined ||
      !addressArray?.thoroughfare ||
      !addressArray?.town_or_city ||
      addressArray?.postcode?.length < 4
    ) {
      setEmptyError(true);
    } else {
      setEmptyError(false);
    }
  }, [addressArray]);

  useEffect(() => {
    datadogRum.startView({
      name: "0.3-Landing-Page-loaded(Organic)",
    });
  }, []);

  useEffect(() => {
    const moveInLengthError =
      addressArray?.move_in_date
        ?.split("-")
        .reverse()
        .join("")
        .split(" ")
        .join("").length !== 8;
    const dobLengthError =
      personalArray?.dateOfBirth
        ?.split("-")
        .reverse()
        .join("")
        .split(" ")
        .join("").length !== 8;
    if (
      ageError ||
      personalArray?.mobileNumbers?.length < 10 ||
      personalArray?.mobileNumbers === null ||
      personalArray?.title === null ||
      !isValidEmail(personalArray?.emailAddresses?.trim()) ||
      dobLengthError ||
      dobError ||
      emailError ||
      mobileError ||
      addressError ||
      moveInLengthError ||
      moveError ||
      oldDateError ||
      emptyError
    ) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [
    personalArray,
    addressArray,
    ageError,
    dobError,
    emailError,
    mobileError,
    addressError,
    moveError,
    oldDateError,
    emptyError,
  ]);

  const acceptDetails = async e => {
    e.preventDefault();
    setLoading(true);
    if (altAge) {
      router.push("/credit-card/declined");
    }
    if (verifyComplete) {
      completePersonal(true, personalArray, addressArray);
      setLoading(false);
    }
    if (!altAge && !verifyComplete) {
      const cv = crypto.randomBytes(64).toString("hex");
      const code_challenge = crypto
        .createHash("sha256")
        .update(cv)
        .digest("hex");
      datadogRum.addAction("1.1-Personal-details-success", {
        onboardingChannel: "Organic",
      });
      try {
        const frontendkey = await generateKeyPair();
        const body = {
          onmouuid: "SEED",
          code_challenge: base64url(code_challenge),
          code_challenge_method: "S256",
          frontendkey: frontendkey.FE_PUB_KEY,
        };
        const response = await fetch("/api/onboarding/authinit", {
          method: "POST",
          body: JSON.stringify(body),
        });
        const res = await response.json();
        if (response.status === 200) {
          try {
            const tokens = {
              uuid: res?.onmouuid,
              onmoAT: res?.auth.accessToken,
              onmoRT: res?.auth.refreshToken,
              backendkey: res?.backendkey,
              cv: base64url(cv),
            };
            datadogRum.setUser({
              id: tokens.uuid,
              onboardingChannel: "Organic",
              age:
                new Date().getFullYear() -
                new Date(personalArray.dateOfBirth).getFullYear(),
            });
            setDataInSessionStorage("applicationTokens", tokens);
            const body = {
              onmouuid: tokens.uuid,
              phonenumber: "+44" + personalArray.mobileNumbers,
            };
            const response = await fetch("/api/onboarding/register-number", {
              method: "POST",
              headers: { Authorization: `${tokens.onmoAT}` },
              body: JSON.stringify(body),
            });
            if (response.status === 200) {
              datadogRum.addAction("1.2-Personal-details-confirmed", {
                onboardingChannel: "Organic",
              });
              completePersonal(true, personalArray, addressArray);
              setLoading(false);
              const tagManagerArgs = {
                gtmId: "GTM-PVX89V7G",
                dataLayer: {
                  event: "land-on-apply",
                },
              };
              TagManager.initialize(tagManagerArgs);
            } else if (response.status === 409) {
              router.push("/credit-card/declined");
            } else {
              router.push("/credit-card/error", "/");
            }
          } catch (err) {
            console.error(err);
            router.push("/credit-card/error", "/");
          }
        } else {
          console.error("authinit failed");
          router.push("/credit-card/error", "/");
          //authinit failed
        }
      } catch (err) {
        console.error(err);
        router.push("/credit-card/error", "/");
        //try failed
      }
      setLoading(false);
    }
  };

  const autoFillAddress = async (e, postcode) => {
    if (e === "noResult" || e === null || e?.noResults === true) {
      const newArray = {
        ...ADDRESS_DATA_FIELDS_EMPTY,
        postcode,
        country: undefined,
        move_in_date: addressArray?.move_in_date,
      };
      setAddressArray(newArray);
    } else {
      const newArray = {
        ...Object.fromEntries(
          ADDRESS_DATA_FIELDS.map(addressField => [
            addressField,
            e[addressField],
          ]),
        ),
        postcode,
        move_in_date: addressArray?.move_in_date,
        country: e?.country,
      };
      setReturnedAddress(e);
      setAddressArray(newArray);
      setEmptyError(false);
      setAddressError(false);
    }
  };

  useEffect(() => {
    if (!dobDisplay) return;
    const dobElements = dobDisplay.split("/");

    const dobTextDashSeperated = dobElements.reverse().join("-");
    setPersonalArray(old => ({
      ...old,
      dateOfBirth: dobTextDashSeperated,
    }));

    // check a fully formed date has been entered
    if (
      dobElements.filter(element => Boolean(String(element).trim())).length < 3
    )
      return;

    const dobValue = dobElements.map(s => s.trim()).join("");
    if (dobValue.length < 8) return;

    const birthDate = new Date(dobTextDashSeperated);

    const age = calcAge(birthDate);

    if (age <= 0) {
      setDobError(true);
    } else if (age < 18) setAgeError(true);
    else if (age >= 18 && age <= 23) {
      setAgeError(false);
      setAltAge(true);
    } else {
      setDobError(false);
      setAgeError(false);
      setAltAge(false);
    }
  }, [dobDisplay]);

  return (
    <Container onSubmit={acceptDetails}>
      <p className={styles.topTitle}>Let&apos;s start with the basics</p>
      <Box gap={10} hideBoxMobile>
        <div className="flex flex-col gap-1">
          <label htmlFor="title" className={styles.inputTitle}>
            Title <span className="text-onmoRed">*</span>
          </label>
          <div className="relative w-full">
            <CustomSelect
              id="title"
              value={personalArray?.title}
              onChange={e => setPersonalArray(old => ({ ...old, title: e }))}
              renderValue={renderValue}
            >
              {titleOptions.map((v, i) => (
                <StyledOption key={i} value={v.value}>
                  {v.label}
                </StyledOption>
              ))}
            </CustomSelect>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-5 md:gap-2.5">
          <BasicInput
            label="First name"
            name="given-name"
            type="text"
            value={personalArray?.firstName}
            onChange={e => parentArray(e, "firstName", "personal")}
            hasError={e => setEmptyError(e)}
          />
          <BasicInput
            label="Last name"
            name="family-name"
            type="text"
            value={personalArray?.lastName}
            onChange={e => parentArray(e, "lastName", "personal")}
            hasError={e => setEmptyError(e)}
          />
        </div>
        <PostcodeSearch passAddress={autoFillAddress} />

        <BasicInput
          label="Apartment/Flat Number"
          type="text"
          value={addressArray?.sub_building_number}
          onChange={e => parentArray(e, "sub_building_number")}
          hasError={e => setEmptyError(e)}
          optional={!returnedAddress?.sub_building_number}
        />
        <BasicInput
          label={"House/Building name"}
          type="text"
          value={addressArray.building_name}
          onChange={e => parentArray(e, "building_name")}
          hasError={e => setEmptyError(e)}
          optional={!returnedAddress?.building_name}
        />
        <BasicInput
          label={"House/Building No."}
          type="text"
          value={addressArray.building_number}
          onChange={e => parentArray(e, "building_number")}
          hasError={e => setEmptyError(e)}
          optional={!returnedAddress?.building_number}
        />
        <BasicInput
          label="Street"
          type="text"
          value={addressArray?.thoroughfare}
          onChange={e => parentArray(e, "thoroughfare")}
          hasError={e => setEmptyError(e)}
        />
        <BasicInput
          label="Town/City"
          type="text"
          value={addressArray?.town_or_city}
          onChange={e => parentArray(e, "town_or_city")}
          hasError={e => setEmptyError(e)}
        />
        <div className="flex flex-col gap-1">
          <label htmlFor="move-in" className={styles.inputTitle}>
            Move in date <span className="text-onmoRed">*</span>
          </label>
          <NumberFormat
            id="move-in"
            aria-autocomplete="none"
            value={moveInDateDisplay}
            placeholder="DD/MM/YYYY"
            onBlur={() => {
              const isValidDate = checkValidDate(addressArray?.move_in_date);
              const isVeryOldDate = checkValidOldDate(
                addressArray?.move_in_date,
              );
              if (!isValidDate) {
                setMoveError(true);
              } else if (isVeryOldDate) {
                setOldDateError(true);
              } else {
                setMoveError(false);
                setOldDateError(false);
              }
            }}
            onValueChange={v => {
              setMoveDateDisplay(v.formattedValue);
            }}
            format="##/##/####"
            className={styles.defaultInput}
          />
          {moveError ? (
            <p className="text-onmoRed">
              Hmm&#x2C; something doesn&apos;t look right here.
            </p>
          ) : oldDateError ? (
            <p className="text-onmoRed">
              Move in Date must be later than 01/01/1900
            </p>
          ) : null}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="dob" className={styles.inputTitle}>
            Date of birth <span className="text-onmoRed">*</span>
          </label>
          <NumberFormat
            id="dob"
            // Disable browser form autocomplete in MS Edge
            aria-autocomplete="none"
            value={dobDisplay.split("/").join("")}
            placeholder="DD/MM/YYYY"
            onBlur={() => {
              const isValidDate = checkValidDate(personalArray?.dateOfBirth);
              if (!isValidDate) {
                setDobError(true);
              } else {
                setDobError(false);
              }
            }}
            onValueChange={v => setDOBdisplay(v.formattedValue)}
            format="##/##/####"
            className={styles.defaultInput}
          />
          {ageError ? (
            <p className="text-onmoRed">
              Sorry, you need to be over 18 to continue.
            </p>
          ) : null}
          {dobError ? (
            <p className="text-onmoRed">
              Hmm&#x2C; something doesn&apos;t look right here.
            </p>
          ) : null}
        </div>
        <BasicInput
          label="Email address"
          type="email"
          value={personalArray?.emailAddresses}
          errorMessage="Please check your email address"
          error={emailError}
          onInputCheck={(e, onChange, setDisplayError) => {
            setDisplayError(false);
            const re = /^[a-zA-Z0-9&@.$%\-,():;`_]+$/;
            if (re.test(e.target.value)) {
              onChange(e);
            }
            if (re.test(e.target.value) === false && e.target.value === "") {
              onChange(e);
            }
          }}
          onChange={e => {
            setPersonalArray(old => ({
              ...old,
              emailAddresses: e.target.value,
            }));

            if (emailError) {
              setEmailError(!isValidEmail(e.target.value));
            }
          }}
          hasError={e => setEmptyError(e)}
          onBlur={e => {
            setEmailError(!isValidEmail(e.target.value));
          }}
        />
        <div className="flex flex-col gap-1">
          <label htmlFor="mobNo" className={styles.inputTitle}>
            Mobile number <span className="text-onmoRed">*</span>
          </label>
          <NumberFormat
            id="mobNo"
            disabled={verifyComplete}
            allowEmptyFormatting
            format="+44 (0) #### ### ###"
            className={styles.defaultInput}
            value={personalArray?.mobileNumbers}
            allowNegative={false}
            type="tel"
            onValueChange={v => {
              setPersonalArray(old => ({
                ...old,
                mobileNumbers: v.value,
              }));

              if (v.value[0] !== "7") {
                setMobileError(true);
              } else {
                setMobileError(false);
              }
            }}
            mask="-"
          />
          {mobileError ? (
            <p className={styles.inputErrorMessage}>
              Please enter a valid mobile number format
            </p>
          ) : null}
        </div>

        <Button disabled={disabled} loading={loading}>
          {verifyComplete ? "Update details" : "Next step"}
        </Button>
      </Box>
      <CustomWarning>
        <div className="text-xs text-secondaryGrey flex flex-col gap-2 pl-[28px]">
          <p>
            The personal information we collect from you will be shared with
            fraud prevention agencies who will use it to prevent fraud and
            money-laundering and to verify your identity.
          </p>
          <p>
            If fraud is detected, you could be refused certain services,
            finance, or employment.
          </p>
          <p>
            Further details of how your information will be used by us and these
            fraud prevention agencies, and your data protection rights, can be
            found by <a className="underline">Cifas</a>.
          </p>
        </div>
      </CustomWarning>
    </Container>
  );
}

const titleOptions = [
  {
    label: "Mr",
    value: "mr",
  },
  {
    label: "Mrs",
    value: "mrs",
  },
  {
    label: "Ms",
    value: "ms",
  },
  {
    label: "Miss",
    value: "miss",
  },
  {
    label: "Dr",
    value: "dr",
  },
  {
    label: "Doctor",
    value: "doctor",
  },
  {
    label: "Professor",
    value: "professor",
  },
  {
    label: "Lady",
    value: "lady",
  },
  {
    label: "Lord",
    value: "lord",
  },
  {
    label: "Mx",
    value: "mx",
  },
  {
    label: "HRH",
    value: "hrh",
  },
  {
    label: "Unknown",
    value: "unknown",
  },
];
